import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { API } from "../lib/api";

const publicApi = axios.create({ baseURL: API, withCredentials: false });

export default function PublicForm() {
  const params = useParams();
  // Route can match either /forms/:token (public) or /forms/invite/:invite_token.
  const inviteToken = params.invite_token;
  const token = params.token;

  const [schema, setSchema] = useState(null);
  const [answers, setAnswers] = useState({});
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const fetchUrl = inviteToken ? `/public/forms/invite/${inviteToken}` : `/public/forms/${token}`;
  const submitUrl = inviteToken ? `/public/forms/invite/${inviteToken}/submit` : `/public/forms/${token}/submit`;

  useEffect(() => {
    publicApi
      .get(fetchUrl)
      .then((r) => {
        setSchema(r.data);
        if (r.data?.invite?.email) {
          setAnswers((a) => ({ ...a, submitter_email: r.data.invite.email }));
        }
      })
      .catch((e) => setError(e.response?.data?.detail || "This form is no longer available."));
  }, [fetchUrl]);

  const update = (key, value) => setAnswers((a) => ({ ...a, [key]: value }));

  // Phase 3.48 — robust submit handler. We treat any 2xx as success and ONLY
  // surface a failure when the network call genuinely throws / returns 4xx.
  // The previous handler was logically correct but lacked a success toast,
  // so users in a flaky network reported "looks like nothing happened".
  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return; // guard against double-submit race
    setSubmitting(true);
    try {
      const res = await publicApi.post(submitUrl, { answers });
      if (res.status >= 200 && res.status < 300 && res.data && res.data.ok !== false) {
        setDone(true);
        toast.success("Submission received");
      } else {
        toast.error("We couldn't confirm your submission. Please try again.");
      }
    } catch (err) {
      const msg = err.response?.data?.detail
        || (err.message === "Network Error" ? "Network problem — please retry." : null)
        || "Couldn't submit — please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
        <div className="max-w-md text-center bg-white border border-slate-200 p-10 rounded-sm" data-testid="public-form-error">
          <Lock className="w-10 h-10 mx-auto text-slate-400" />
          <h1 className="font-heading text-2xl mt-4">Form unavailable</h1>
          <p className="text-slate-600 mt-2 text-sm">{error}</p>
        </div>
      </div>
    );
  }
  if (!schema) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;

  // Phase 3.48 — Org-branded chrome. When the form payload carries
  // `organization`, we replace the Array Engine header with the org's
  // own logo + name. Falls back to a neutral header if branding is missing.
  const org = schema.organization || null;

  if (done) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
        <div className="max-w-md text-center bg-white border border-slate-200 p-10 rounded-sm" data-testid="public-form-done">
          <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500" />
          <h1 className="font-heading text-2xl mt-4">Thanks for your response.</h1>
          <p className="text-slate-600 mt-2 text-sm">Your submission has been recorded. You can close this tab.</p>
        </div>
      </div>
    );
  }

  const fields = schema.form.fields || [];
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white" data-testid="public-form-org-header">
        <div className="max-w-2xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {org?.logo ? (
              <img src={org.logo} alt={org.name || "Organisation"} className="h-9 w-auto object-contain" data-testid="org-logo" />
            ) : null}
            {org?.name && (
              <span className="font-heading text-base font-semibold text-slate-900" data-testid="org-name">{org.name}</span>
            )}
          </div>
          <span className="hidden sm:flex items-center gap-1 text-xs text-slate-500">
            <ShieldCheck className="w-3 h-3" /> Submission is securely logged
          </span>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-10" data-testid="public-form">
        <header className="mb-8">
          <h1 className="font-heading text-3xl font-semibold text-slate-900">{schema.form.name}</h1>
          {schema.form.description && (
            <p className="text-slate-600 mt-2 leading-relaxed">{schema.form.description}</p>
          )}
          {schema.invite?.email && (
            <p className="text-xs text-emerald-700 mt-3 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-sm inline-block">
              Personal invite for {schema.invite.email}
            </p>
          )}
        </header>

        {/* noValidate so HTML5 native input rules can't block valid numeric/
            decimal inputs. Server is the source of truth. */}
        <form onSubmit={submit} noValidate className="space-y-5 bg-white border border-slate-200 rounded-sm p-6">
          {fields.map((f) => (
            <FieldRenderer key={f.id || f.key} field={f} value={answers[f.key]} onChange={(v) => update(f.key, v)} />
          ))}

          <div className="border-t border-slate-100 pt-4 mt-4 space-y-3 text-sm text-slate-500">
            <p className="label-overline">Your details (optional)</p>
            <Input
              placeholder="Your name (optional)"
              value={answers.submitter_name || ""}
              onChange={(e) => update("submitter_name", e.target.value)}
              data-testid="submitter-name"
            />
            <Input
              type="email"
              placeholder="Your email (optional)"
              value={answers.submitter_email || ""}
              onChange={(e) => update("submitter_email", e.target.value)}
              data-testid="submitter-email"
              disabled={!!schema.invite?.email}
            />
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-medium"
            data-testid="public-form-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {submitting ? "Submitting…" : "Submit response"}
          </Button>
          {/* Phase 3.48 — Array Engine attribution removed from public
              form footer. The org owns the surface. */}
        </form>
      </main>
    </div>
  );
}

function FieldRenderer({ field, value, onChange }) {
  const required = !!field.required;
  const common = {
    "data-testid": `field-${field.key}`,
    required,
    value: value ?? "",
    onChange: (e) => onChange(e.target.value),
  };
  const label = (
    <Label className="text-sm font-medium text-slate-700 flex items-center gap-1">
      {field.label}
      {required && <span className="text-rose-500" aria-hidden>*</span>}
    </Label>
  );
  const help = field.help ? <p className="text-[11px] text-slate-400 mt-1">{field.help}</p> : null;

  switch (field.type) {
    case "textarea":
      return (
        <div>
          {label}
          <Textarea placeholder={field.placeholder || ""} rows={4} className="mt-1.5" {...common} />
          {help}
        </div>
      );
    case "number":
      // Phase 3.48 — generic number field. NO min/max so any valid
      // decimal/integer/negative is accepted. `step="any"` lets the
      // browser keep arrow-stepping for power-users without rejecting
      // "3.14159" type inputs.
      return (
        <div>
          {label}
          <Input
            type="number"
            step="any"
            inputMode="decimal"
            placeholder={field.placeholder || ""}
            className="mt-1.5"
            {...common}
          />
          {help}
        </div>
      );
    case "select":
      return (
        <div>
          {label}
          <select className="mt-1.5 w-full border border-slate-200 rounded-md px-3 py-2 text-sm" {...common}>
            <option value="">Select…</option>
            {(field.options || []).map((o) => (
              <option key={o.value || o} value={o.value || o}>{o.label || o.value || o}</option>
            ))}
          </select>
          {help}
        </div>
      );
    case "radio":
      return (
        <div>
          {label}
          <div className="mt-2 space-y-1.5">
            {(field.options || []).map((o) => {
              const val = o.value || o;
              return (
                <label key={val} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name={field.key} value={val} checked={value === val} onChange={(e) => onChange(e.target.value)} className="accent-emerald-500" />
                  <span>{o.label || val}</span>
                </label>
              );
            })}
          </div>
          {help}
        </div>
      );
    case "checkbox":
      return (
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
            className="mt-1 accent-emerald-500"
            data-testid={`field-${field.key}`}
          />
          <div>
            <span className="text-sm font-medium text-slate-700">{field.label}{required && <span className="text-rose-500 ml-0.5">*</span>}</span>
            {help}
          </div>
        </label>
      );
    case "datetime":
      return (
        <div>
          {label}
          <Input type="datetime-local" className="mt-1.5" {...common} />
          {help}
        </div>
      );
    case "range":
      // Phase 3.48 — keep slider semantics but DON'T pass HTML5 min/max
      // unless the form author explicitly set them. Defaults previously
      // came in as 0/100 which rejected negative numbers + decimals.
      return (
        <div>
          {label}
          <Input
            type="number"
            step={field.step || "any"}
            inputMode="decimal"
            min={field.min ?? undefined}
            max={field.max ?? undefined}
            placeholder={field.placeholder || ""}
            className="mt-1.5"
            {...common}
          />
          {help}
        </div>
      );
    case "location":
      return (
        <div>
          {label}
          <Input placeholder={field.placeholder || "City, region"} className="mt-1.5" {...common} />
          {help}
        </div>
      );
    case "hidden":
      return <input type="hidden" name={field.key} value={value || field.placeholder || ""} />;
    default:
      return (
        <div>
          {label}
          <Input type={field.type || "text"} placeholder={field.placeholder || ""} className="mt-1.5" {...common} />
          {help}
        </div>
      );
  }
}
